import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  Image,
  useColorScheme,
  TextInput,
  Modal,
  Alert,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

// 从搜索页面引入模拟数据
const mockFoodData = [
  {
    id: '1',
    name: '米饭',
    pinyin: 'mifan',
    category: '谷物类',
    nutrients: { calories: 116, protein: 2.6, carbs: 25.6, fat: 0.3 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '2',
    name: '全麦面包',
    pinyin: 'quanmaimianbao',
    category: '谷物类',
    nutrients: { calories: 265, protein: 9.0, carbs: 49.0, fat: 3.2 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '3',
    name: '鸡胸肉',
    pinyin: 'jixiongrou',
    category: '肉类',
    nutrients: { calories: 165, protein: 31.0, carbs: 0, fat: 3.6 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
];

const FoodFavoritesScreen = ({ route, navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const { tab = 'favorites' } = route.params || {};
  const [activeTab, setActiveTab] = useState(tab);
  const [favorites, setFavorites] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [customFoods, setCustomFoods] = useState([]);
  const [showAddFoodModal, setShowAddFoodModal] = useState(false);
  const [newFoodData, setNewFoodData] = useState({
    name: '',
    category: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    portionSize: '',
    portionUnit: '克',
  });
  
  useEffect(() => {
    // 加载数据（在实际应用中，这些数据应该从本地存储或数据库中获取）
    loadData();
  }, []);
  
  // 模拟加载数据
  const loadData = () => {
    // 假设这些是从持久化存储中加载的
    setFavorites(mockFoodData.slice(0, 2));
    setRecentlyViewed(mockFoodData.slice(0, 3));
    setCustomFoods([
      {
        id: 'custom1',
        name: '自制沙拉',
        category: '混合食物',
        nutrients: { calories: 220, protein: 10, carbs: 15, fat: 12 },
        imageUrl: 'https://via.placeholder.com/60',
        portion: { default: 200, unit: '克' },
        isCustom: true,
      },
    ]);
  };
  
  // 渲染食物列表项
  const renderFoodItem = ({ item }) => (
    <TouchableOpacity
      style={{
        flexDirection: 'row',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: isDarkMode ? '#7284a8' : '#e5e7eb',
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        borderRadius: 8,
        marginBottom: 8,
      }}
      onPress={() => navigation.navigate('FoodDetail', { foodId: item.id })}
    >
      <Image
        source={{ uri: item.imageUrl }}
        style={{
          width: 60,
          height: 60,
          borderRadius: 8,
          marginRight: 16,
        }}
      />
      <View style={{ flex: 1 }}>
        <Text style={{
          fontSize: 16,
          fontWeight: 'bold',
          marginBottom: 4,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          {item.name}
        </Text>
        <Text style={{
          fontSize: 14,
          color: isDarkMode ? '#e0e0e0' : '#6b7280',
          marginBottom: 4,
        }}>
          {item.category}
        </Text>
        <Text style={{
          fontSize: 14,
          color: isDarkMode ? '#e0e0e0' : '#6b7280',
        }}>
          {item.nutrients.calories} 千卡 / {item.portion.default}{item.portion.unit}
        </Text>
      </View>
      
      {activeTab === 'custom' && item.isCustom ? (
        <TouchableOpacity
          onPress={() => handleDeleteCustomFood(item.id)}
          style={{ padding: 8 }}
        >
          <Ionicons name="trash-outline" size={24} color={isDarkMode ? '#e0e0e0' : '#ef4444'} />
        </TouchableOpacity>
      ) : (
        <Ionicons name="chevron-forward" size={24} color={isDarkMode ? '#e0e0e0' : '#9ca3af'} />
      )}
    </TouchableOpacity>
  );
  
  // 删除自定义食物
  const handleDeleteCustomFood = (id) => {
    Alert.alert(
      '确认删除',
      '确定要删除这个自定义食物吗？',
      [
        {
          text: '取消',
          style: 'cancel',
        },
        {
          text: '删除',
          onPress: () => {
            const updatedCustomFoods = customFoods.filter(food => food.id !== id);
            setCustomFoods(updatedCustomFoods);
            // 在实际应用中，这里应该更新本地存储或数据库
          },
          style: 'destructive',
        },
      ]
    );
  };
  
  // 添加自定义食物
  const handleAddCustomFood = () => {
    // 表单验证
    if (!newFoodData.name.trim()) {
      Alert.alert('提示', '请输入食物名称');
      return;
    }
    
    if (!newFoodData.category.trim()) {
      Alert.alert('提示', '请输入食物分类');
      return;
    }
    
    const calories = parseFloat(newFoodData.calories);
    const protein = parseFloat(newFoodData.protein);
    const carbs = parseFloat(newFoodData.carbs);
    const fat = parseFloat(newFoodData.fat);
    const portionSize = parseFloat(newFoodData.portionSize);
    
    if (isNaN(calories) || calories < 0) {
      Alert.alert('提示', '请输入有效的卡路里值');
      return;
    }
    
    if (isNaN(protein) || protein < 0) {
      Alert.alert('提示', '请输入有效的蛋白质值');
      return;
    }
    
    if (isNaN(carbs) || carbs < 0) {
      Alert.alert('提示', '请输入有效的碳水化合物值');
      return;
    }
    
    if (isNaN(fat) || fat < 0) {
      Alert.alert('提示', '请输入有效的脂肪值');
      return;
    }
    
    if (isNaN(portionSize) || portionSize <= 0) {
      Alert.alert('提示', '请输入有效的份量大小');
      return;
    }
    
    // 创建新的自定义食物
    const newFood = {
      id: `custom${Date.now()}`,
      name: newFoodData.name.trim(),
      category: newFoodData.category.trim(),
      nutrients: {
        calories,
        protein,
        carbs,
        fat,
      },
      imageUrl: 'https://via.placeholder.com/60',
      portion: {
        default: portionSize,
        unit: newFoodData.portionUnit,
      },
      isCustom: true,
    };
    
    setCustomFoods([...customFoods, newFood]);
    
    // 重置表单
    setNewFoodData({
      name: '',
      category: '',
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
      portionSize: '',
      portionUnit: '克',
    });
    
    setShowAddFoodModal(false);
    // 在实际应用中，这里应该更新本地存储或数据库
  };
  
  // 从收藏删除
  const handleRemoveFromFavorites = (id) => {
    const updatedFavorites = favorites.filter(food => food.id !== id);
    setFavorites(updatedFavorites);
    // 在实际应用中，这里应该更新本地存储或数据库
  };
  
  // 从最近查看删除
  const handleRemoveFromRecent = (id) => {
    const updatedRecent = recentlyViewed.filter(food => food.id !== id);
    setRecentlyViewed(updatedRecent);
    // 在实际应用中，这里应该更新本地存储或数据库
  };
  
  // 渲染添加自定义食物的模态窗口
  const renderAddFoodModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={showAddFoodModal}
      onRequestClose={() => setShowAddFoodModal(false)}
    >
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}>
        <View style={{
          width: '90%',
          backgroundColor: isDarkMode ? '#8fa0c1' : 'white',
          borderRadius: 12,
          padding: 20,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 5,
          maxHeight: '80%',
        }}>
          <ScrollView>
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              marginBottom: 20,
              color: isDarkMode ? 'white' : '#1f2937',
              textAlign: 'center',
            }}>
              添加自定义食物
            </Text>
            
            <Text style={{
              fontSize: 16,
              marginBottom: 4,
              color: isDarkMode ? 'white' : '#4b5563',
            }}>
              食物名称
            </Text>
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                color: isDarkMode ? 'white' : '#1f2937',
                backgroundColor: isDarkMode ? '#7284a8' : 'white',
              }}
              placeholder="输入食物名称"
              placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
              value={newFoodData.name}
              onChangeText={(text) => setNewFoodData({ ...newFoodData, name: text })}
            />
            
            <Text style={{
              fontSize: 16,
              marginBottom: 4,
              color: isDarkMode ? 'white' : '#4b5563',
            }}>
              食物分类
            </Text>
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                color: isDarkMode ? 'white' : '#1f2937',
                backgroundColor: isDarkMode ? '#7284a8' : 'white',
              }}
              placeholder="输入食物分类"
              placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
              value={newFoodData.category}
              onChangeText={(text) => setNewFoodData({ ...newFoodData, category: text })}
            />
            
            <Text style={{
              fontSize: 16,
              marginBottom: 4,
              color: isDarkMode ? 'white' : '#4b5563',
            }}>
              每份营养成分
            </Text>
            
            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
              <View style={{ flex: 1, marginRight: 8 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  份量大小
                </Text>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                    borderRadius: 8,
                    padding: 12,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#7284a8' : 'white',
                  }}
                  placeholder="100"
                  placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                  value={newFoodData.portionSize}
                  onChangeText={(text) => setNewFoodData({ ...newFoodData, portionSize: text })}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  单位
                </Text>
                <View style={{
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                  borderRadius: 8,
                  padding: 12,
                  backgroundColor: isDarkMode ? '#7284a8' : 'white',
                }}>
                  <Text style={{ color: isDarkMode ? 'white' : '#1f2937' }}>
                    {newFoodData.portionUnit}
                  </Text>
                </View>
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', marginBottom: 16 }}>
              <View style={{ flex: 1, marginRight: 8 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  卡路里 (kcal)
                </Text>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                    borderRadius: 8,
                    padding: 12,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#7284a8' : 'white',
                  }}
                  placeholder="0"
                  placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                  value={newFoodData.calories}
                  onChangeText={(text) => setNewFoodData({ ...newFoodData, calories: text })}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  蛋白质 (g)
                </Text>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                    borderRadius: 8,
                    padding: 12,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#7284a8' : 'white',
                  }}
                  placeholder="0"
                  placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                  value={newFoodData.protein}
                  onChangeText={(text) => setNewFoodData({ ...newFoodData, protein: text })}
                  keyboardType="numeric"
                />
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', marginBottom: 20 }}>
              <View style={{ flex: 1, marginRight: 8 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  碳水化合物 (g)
                </Text>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                    borderRadius: 8,
                    padding: 12,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#7284a8' : 'white',
                  }}
                  placeholder="0"
                  placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                  value={newFoodData.carbs}
                  onChangeText={(text) => setNewFoodData({ ...newFoodData, carbs: text })}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  marginBottom: 4,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  脂肪 (g)
                </Text>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                    borderRadius: 8,
                    padding: 12,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#7284a8' : 'white',
                  }}
                  placeholder="0"
                  placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                  value={newFoodData.fat}
                  onChangeText={(text) => setNewFoodData({ ...newFoodData, fat: text })}
                  keyboardType="numeric"
                />
              </View>
            </View>
            
            <View style={{ flexDirection: 'row' }}>
              <TouchableOpacity
                style={{
                  flex: 1,
                  padding: 16,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#7284a8' : '#f3f4f6',
                  alignItems: 'center',
                  marginRight: 8,
                }}
                onPress={() => setShowAddFoodModal(false)}
              >
                <Text style={{
                  fontWeight: '600',
                  color: isDarkMode ? 'white' : '#4b5563',
                }}>
                  取消
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  flex: 1,
                  padding: 16,
                  borderRadius: 8,
                  backgroundColor: '#2563eb',
                  alignItems: 'center',
                }}
                onPress={handleAddCustomFood}
              >
                <Text style={{
                  fontWeight: '600',
                  color: 'white',
                }}>
                  保存
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
  
  return (
    <View style={{ 
      flex: 1, 
      backgroundColor: isDarkMode ? '#8fa0c1' : '#f3f4f6', 
      padding: 16,
    }}>
      {/* 标签切换器 */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        borderRadius: 8,
        padding: 4,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 6,
            backgroundColor: activeTab === 'favorites' ? '#2563eb' : 'transparent',
            alignItems: 'center',
          }}
          onPress={() => setActiveTab('favorites')}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'favorites' ? 'white' : (isDarkMode ? 'white' : '#6b7280'),
          }}>
            收藏
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 6,
            backgroundColor: activeTab === 'recent' ? '#2563eb' : 'transparent',
            alignItems: 'center',
          }}
          onPress={() => setActiveTab('recent')}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'recent' ? 'white' : (isDarkMode ? 'white' : '#6b7280'),
          }}>
            最近使用
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 6,
            backgroundColor: activeTab === 'custom' ? '#2563eb' : 'transparent',
            alignItems: 'center',
          }}
          onPress={() => setActiveTab('custom')}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'custom' ? 'white' : (isDarkMode ? 'white' : '#6b7280'),
          }}>
            自定义食物
          </Text>
        </TouchableOpacity>
      </View>
      
      {/* 内容区域 */}
      {activeTab === 'favorites' && (
        <FlatList
          data={favorites}
          keyExtractor={(item) => item.id}
          renderItem={renderFoodItem}
          ListEmptyComponent={
            <View style={{ padding: 32, alignItems: 'center' }}>
              <Ionicons 
                name="heart-outline" 
                size={64} 
                color={isDarkMode ? '#e0e0e0' : '#9ca3af'} 
                style={{ marginBottom: 16 }}
              />
              <Text style={{
                fontSize: 16,
                fontWeight: '500',
                color: isDarkMode ? 'white' : '#4b5563',
                textAlign: 'center',
              }}>
                您还没有收藏任何食物
              </Text>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                textAlign: 'center',
                marginTop: 8,
                marginBottom: 16,
              }}>
                浏览食物时点击心形图标即可加入收藏
              </Text>
              <TouchableOpacity
                style={{
                  padding: 12,
                  backgroundColor: '#2563eb',
                  borderRadius: 8,
                }}
                onPress={() => navigation.navigate('FoodSearch')}
              >
                <Text style={{ color: 'white', fontWeight: '600' }}>
                  浏览食物库
                </Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}
      
      {activeTab === 'recent' && (
        <FlatList
          data={recentlyViewed}
          keyExtractor={(item) => item.id}
          renderItem={renderFoodItem}
          ListEmptyComponent={
            <View style={{ padding: 32, alignItems: 'center' }}>
              <Ionicons 
                name="time-outline" 
                size={64} 
                color={isDarkMode ? '#e0e0e0' : '#9ca3af'} 
                style={{ marginBottom: 16 }}
              />
              <Text style={{
                fontSize: 16,
                fontWeight: '500',
                color: isDarkMode ? 'white' : '#4b5563',
                textAlign: 'center',
              }}>
                您还没有查看过任何食物
              </Text>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                textAlign: 'center',
                marginTop: 8,
                marginBottom: 16,
              }}>
                浏览食物详情后会自动记录在这里
              </Text>
              <TouchableOpacity
                style={{
                  padding: 12,
                  backgroundColor: '#2563eb',
                  borderRadius: 8,
                }}
                onPress={() => navigation.navigate('FoodSearch')}
              >
                <Text style={{ color: 'white', fontWeight: '600' }}>
                  浏览食物库
                </Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}
      
      {activeTab === 'custom' && (
        <>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}>
            <TouchableOpacity
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: '#2563eb',
                padding: 12,
                borderRadius: 8,
              }}
              onPress={() => setShowAddFoodModal(true)}
            >
              <Ionicons name="add" size={20} color="white" style={{ marginRight: 4 }} />
              <Text style={{ color: 'white', fontWeight: '600' }}>添加自定义食物</Text>
            </TouchableOpacity>
          </View>
          
          <FlatList
            data={customFoods}
            keyExtractor={(item) => item.id}
            renderItem={renderFoodItem}
            ListEmptyComponent={
              <View style={{ padding: 32, alignItems: 'center' }}>
                <Ionicons 
                  name="restaurant-outline" 
                  size={64} 
                  color={isDarkMode ? '#e0e0e0' : '#9ca3af'} 
                  style={{ marginBottom: 16 }}
                />
                <Text style={{
                  fontSize: 16,
                  fontWeight: '500',
                  color: isDarkMode ? 'white' : '#4b5563',
                  textAlign: 'center',
                }}>
                  您还没有添加自定义食物
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                  textAlign: 'center',
                  marginTop: 8,
                }}>
                  点击上方的"添加自定义食物"按钮创建自己的食物
                </Text>
              </View>
            }
          />
        </>
      )}
      
      {renderAddFoodModal()}
    </View>
  );
};

export default FoodFavoritesScreen; 